import { Dialog } from "@dash/dialog";
import { useDeviceType } from "@dash/mq";
import { useToast } from "@dash/toast";
import Decimal from "decimal.js";
import { useQueryClient } from "react-query";

import { Currency, ItemDoc } from "@megaron/catalog-contracts";
import { AddDraftLineInput, OrderDraftLineDto, UpdateDraftLineInput } from "@megaron/crm-contracts";
import { useClientManager } from "@megaron/react-clients";
import { Uuid } from "@megaron/uuid";

import { ItemDialogContent } from "./ItemDialogContent";

type Props = {
  onClose: () => void;
  line: OrderDraftLineDto;
  orderUuid?: Uuid;
  currency: Currency;
  queryKey?: string | string[];
  items?: ItemDoc[];
  priceList?: string;
  baseDiscount?: Decimal;
};

export const EditItemDialog: React.FC<Props> = ({
  onClose,
  line,
  orderUuid,
  queryKey,
  currency,
  items,
  priceList,
  baseDiscount,
}) => {
  const { isMobile } = useDeviceType();

  const toast = useToast();

  const queryClient = useQueryClient();

  const getBasePrice = (itemId?: string) => {
    if (!items || !priceList || !itemId) {
      return undefined;
    }

    return items.find((item) => item.itemId === itemId)?.currentPrices[priceList]?.price;
  };

  const updateLineMutation = useClientManager("crm").updateDraftLine().useMutation();
  const removeLineMutation = useClientManager("crm").removeDraftLine().useMutation();

  const handleEditLine = (line: AddDraftLineInput) => {
    if (!orderUuid) {
      return;
    }

    const input: UpdateDraftLineInput & { uuid: Uuid; lineUuid: Uuid } = {
      promoPrice: line.promoPrice,
      quantity: line.quantity,
      lineUuid: line.lineUuid,
      uuid: orderUuid,
      lineDiscountRate: line.lineDiscountRate,
    };

    updateLineMutation.mutate(input, {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
        onClose();
      },
      onError: (error) => {
        if (error === "DraftNotOpen") {
          toast.error("Wersja robocza nie posiada statusu: otwarta.");
        }

        if (error === "InvalidLineQuantity") {
          toast.error("Nieprawidłowa ilość przedmiotu.");
        }

        if (error === "OrderNotFound") {
          toast.error("Nie znaleziono zamówienia.");
        }

        if (error === "UserNotOrderOwner") {
          toast.error("Nie jesteś właścicielem zamówienia.");
        }

        if (error === "LineDoesNotExist") {
          toast.error("Przedmiot nie istnieje w zamówieniu.");
        }

        if (error === "InvalidLineDiscountRate") {
          toast.error("Nieprawidłowy rabat na przedmiocie. Suma rabatów nie może przekraczać 100%.");
        }
      },
    });
  };

  const handleRemoveLine = (lineUuid: Uuid) => {
    if (!orderUuid) {
      return;
    }

    removeLineMutation.mutate(
      { uuid: orderUuid, lineUuid },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKey);
          onClose();
        },
        onError: (error) => {
          if (error === "DraftNotOpen") {
            toast.error("Wersja robocza nie posiada statusu: otwarta.");
          }

          if (error === "OrderNotFound") {
            toast.error("Nie znaleziono zamówienia.");
          }

          if (error === "UserNotOrderOwner") {
            toast.error("Nie jesteś właścicielem zamówienia.");
          }

          if (error === "LineDoesNotExist") {
            toast.error("Przedmiot nie istnieje w zamówieniu.");
          }
        },
      },
    );
  };

  return (
    <Dialog
      onClose={onClose}
      placement={isMobile ? "top" : "center"}
      css={{ width: isMobile ? "100%" : "400px" }}
      header={items?.find((item) => item.itemId === line.item.itemId)?.itemName ?? ""}
    >
      <div css={{ maxHeight: "70dvh", overflow: "auto" }}>
        <ItemDialogContent
          item={items?.find((item) => item.itemId === line.item.itemId)}
          onSave={handleEditLine}
          line={line}
          onDelete={() => handleRemoveLine(line.uuid)}
          currency={currency}
          basePrice={getBasePrice(line.item.itemId)}
          baseDiscount={baseDiscount}
        />
      </div>
    </Dialog>
  );
};
